<template>
    <div id="app">
        <div class="app-base" v-if="loaded">
            <template v-if="online">
                <Header @language-change="changeLanguage" />
                <router-view :key="`route-${$route.name}-${renderKey}`"></router-view>
                <Cart v-if="cart" :key="`cart-${renderKey}`" />
                <Footer />
            </template>
            <div v-else class="setup-screen">
                <div class="setup-card">
                    <h1 class="setup-title">Kiosk not configured</h1>
                    <p class="setup-subtitle">
                        Go to the Booqi backoffice to connect this kiosk with your organisation.
                        Use the connection code below.
                    </p>
                    <div class="setup-code" v-if="identifierGroups.length">
                        <span class="setup-code-group" v-for="(group, index) in identifierGroups" :key="index">{{ group }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="app-loading" v-else>
            <div class="app-loading-card">
                <div class="app-loading-spinner"></div>
                <h2 class="app-loading-title">Kiosk is loading</h2>
                <p class="app-loading-subtitle">Please wait while we connect everything.</p>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
html,
body,
#app {
    margin: 0;
    padding: 0;
    height: 100%;
    min-height: 100%;
    overflow: hidden;
}
.app-base {
    height: 100%;
}
.app-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: radial-gradient(circle at top, rgba(35, 49, 66, 0.25), rgba(9, 14, 20, 0.95));
}

.app-loading-card {
    width: min(520px, 100%);
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
    padding: 42px 34px;
    text-align: center;
}

.app-loading-spinner {
    width: 84px;
    height: 84px;
    border: 6px solid rgba(0, 0, 0, 0.12);
    border-top-color: rgba(var(--color-button-background), 1);
    border-radius: 999px;
    margin: 0 auto 22px;
    animation: app-loading-spin 900ms linear infinite;
}

.app-loading-title {
    margin: 0;
    font-size: 34px;
    font-weight: 700;
    color: #111;
}

.app-loading-subtitle {
    margin: 12px 0 0;
    font-size: 20px;
    line-height: 1.4;
    color: rgba(17, 17, 17, 0.7);
}

@keyframes app-loading-spin {
    to {
        transform: rotate(360deg);
    }
}

.setup-screen {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background: #f5f6f8;
}

.setup-card {
    width: min(900px, 100%);
    background: #fff;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    padding: 40px;
    text-align: center;

    @media (max-width: 768px) {
        padding: 24px;
    }
}

.setup-title {
    font-size: 42px;
    font-weight: 700;
    margin: 0;

    @media (max-width: 768px) {
        font-size: 32px;
    }
}

.setup-subtitle {
    font-size: 18px;
    margin: 16px 0 0;
    color: rgba(0, 0, 0, 0.7);
}

.setup-code {
    margin-top: 28px;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    justify-content: center;
}

.setup-code-group {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 48px;
    font-weight: 700;
    line-height: 1;
    padding: 18px 22px;
    border-radius: 12px;
    background: #111;
    color: #fff;
    letter-spacing: 0.08em;

    @media (max-width: 768px) {
        font-size: 34px;
        padding: 14px 18px;
    }
}
</style>

<script>
import merge from "lodash/merge";
import { mapGetters } from "vuex";

import Header from "@/components/general/Header";
import Footer from "@/components/general/Footer";
import Cart from "@/components/Cart";

import {
    FETCH_ORGANISATION,
    FETCH_ONGOING,
    FETCH_PLOTS,
    FETCH_POS,
    FETCH_PRODUCT,
    FETCH_PRODUCTS,
    SET_LANGUAGE
} from "@/store/actions.type";
import { baseMessages, i18n } from "@/i18n";
import { initDeliveryService, initPayEngine, isInitialised, isLocked, lock } from "./store/acts";

export default {
    name: "App",
    components: {
        Header,
        Footer,
        Cart
    },
    data() {
        return {
            loaded: false,
            online: true,
            identifier: null,
            route: null,
            last_touch: 0,
            cart: false,
            root: null,
            retryIntervalId: null,
            booting: false,
            renderKey: 0
        };
    },
    mounted() {
        this.root = document.documentElement;
    },
    created() {
        const watchable = ["catalog", "booking"];

        document.body.addEventListener("click", () => {
            this.last_touch = 0;
        }, true);

        this.route = this.$route.name;
        this.retryBoot();

        setInterval(() => {
            if (watchable.includes(this.$route.name)) {
                if (this.last_touch >= 180) {
                    this.last_touch = 0;
                    window.location.replace("/");
                }
                this.last_touch++;
            }
        }, 1000);
    },
    methods: {
        async boot() {
            try {
                await this.$store.dispatch(FETCH_ORGANISATION);
                await this.$store.dispatch(FETCH_POS);

                const pos = this.getPOS;
                this.applyLanguage(pos);

                const root = this.root || document.documentElement;

                let backgroundValue = "none";
                const backgroundPath = pos && pos.backgroundImage;
                if (typeof backgroundPath === "string") {
                    const cleaned = backgroundPath.trim().replace(/^\/+/, "");
                    if (cleaned.length) {
                        backgroundValue = `url("https://cdn.booqi.app/${cleaned}")`;
                    }
                }
                root.style.setProperty("--pos-background-image", backgroundValue);

                if (pos.online) {
                    let colors = pos.appearance;
                    for (const [name, color] of Object.entries(colors)) {
                        const newName = "--color-" + name.replaceAll("_", "-");
                        root.style.setProperty(newName, this.colorConvert(color));
                    }
                }

                if (pos.acts) {
                    const locked = await isLocked();
                    if (!locked) await lock();

                    const initialized = await isInitialised();
                    if (!initialized) await initPayEngine();

                    await initDeliveryService();
                }

                this.loaded = true;
            } catch (e) {
                console.error("Error in boot process:", e);
                throw e;
            }
        },
        async changeLanguage(language) {
            const selectedLanguage = this.normalizeLanguageCode(language);

            if (!selectedLanguage || selectedLanguage === this.getLanguage) {
                return;
            }

            await this.$store.dispatch(SET_LANGUAGE, selectedLanguage);
            await this.refreshLanguageData();
        },
        async refreshLanguageData() {
            await this.$store.dispatch(FETCH_POS);
            this.applyLanguage(this.getPOS);

            if (this.$route.name === "catalog") {
                await this.$store.dispatch(FETCH_PRODUCTS);
                await this.$store.dispatch(FETCH_ONGOING);
            } else if (this.$route.name === "booking") {
                await this.$store.dispatch(FETCH_PRODUCTS);

                const productId = this.getBooking?.product?.id;
                if (productId !== null && productId !== undefined) {
                    await this.$store.dispatch(FETCH_PRODUCT, productId);
                    await this.$store.dispatch(FETCH_PLOTS);
                }
            } else if (this.$route.name === "confirm") {
                await this.$store.dispatch(FETCH_PRODUCTS);
            }

            if (this.$route.name !== "checkout") {
                this.renderKey += 1;
            }
        },
        normalizeLanguageCode(language) {
            const normalize = (value) => {
                if (typeof value !== "string") {
                    return null;
                }

                const normalized = value.trim().toLowerCase();

                if (!normalized) {
                    return null;
                }

                if (normalized === "ja-jp") {
                    return "ja";
                }

                return normalized;
            };

            if (typeof language === "string") {
                return normalize(language);
            }

            if (!language || typeof language !== "object") {
                return null;
            }

            return [
                language.code,
                language.language,
                language.locale,
                language.key
            ]
                .map((value) => normalize(value))
                .find(Boolean) || null;
        },
        applyLanguage(pos) {
            const locale = this.normalizeLanguageCode(pos?.current_language)
                || this.normalizeLanguageCode(pos?.language)
                || this.getLanguage
                || "nl";
            const fallbackLocale = this.normalizeLanguageCode(pos?.language) || "nl";
            const localeMessages = baseMessages[locale] || {};
            const fallbackMessages = baseMessages[fallbackLocale] || baseMessages.nl || {};
            const currentMessages = i18n.global.getLocaleMessage(locale) || {};
            const apiMessages = pos?.translations && typeof pos.translations === "object"
                ? pos.translations
                : {};

            i18n.global.setLocaleMessage(
                locale,
                merge({}, fallbackMessages, localeMessages, currentMessages, apiMessages)
            );

            if (typeof i18n.global.locale === "object" && "value" in i18n.global.locale) {
                i18n.global.locale.value = locale;
            } else {
                i18n.global.locale = locale;
            }
        },
        async retryBoot() {
            if (this.booting) return;

            this.booting = true;
            try {
                while (true) {
                    try {
                        await this.boot();
                        break;
                    } catch (e) {
                        console.error("Retrying boot in 10 seconds...");
                        await new Promise(resolve => setTimeout(resolve, 10000));
                    }
                }
            } finally {
                this.booting = false;
            }
        },
        colorConvert(hex) {
            let regex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
            hex = hex.replace(regex, function(m, r, g, b) {
                return r + r + g + g + b + b;
            });

            let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

            if (!result) {
                return null;
            }

            return parseInt(result[1], 16) + ", " + parseInt(result[2], 16) + ", " + parseInt(result[3], 16);
        }
    },
    watch: {
        $route(to, from) {
            this.cart = (to.name !== "splash");
        },
        getPOS: {
            deep: true,
            handler(pos) {
                this.online = Boolean(pos && pos.online);
                this.identifier = pos ? pos.identifier : null;

                if (!this.online) {
                    if (!this.retryIntervalId) {
                        this.retryIntervalId = setInterval(() => {
                            this.retryBoot();
                        }, 60 * 1000);
                    }
                } else if (this.retryIntervalId) {
                    clearInterval(this.retryIntervalId);
                    this.retryIntervalId = null;
                }
            }
        }
    },
    computed: {
        identifierGroups() {
            const raw = this.identifier === null || this.identifier === undefined ? "" : String(this.identifier);
            const cleaned = raw.replace(/\s+/g, "").trim();
            const groups = cleaned.match(/.{1,4}/g);
            return Array.isArray(groups) ? groups : [];
        },
        ...mapGetters([
            "getPOS",
            "getOrganisation",
            "getLanguage",
            "getBooking"
        ])
    }
};
</script>
